import { useOneClickTradingV2 } from '@ui/features/OneClickTrading/hooks/useOneClickTradingV2';
import { useInvalidatePositionQuery } from '@ui/features/Trade/hooks/useInvalidateQuery';
import { GenericTradeParams, TpSlValueState } from '@ui/features/Trade/types';
import { useDataChainId } from '@ui/hooks/datachain';
import { useMutationToast } from '@ui/hooks/useMutationToast';
import { useSimulateMutation } from '@ui/hooks/useSimulateMutation';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import { pollV2IntentStatus } from '@ui/services/backend/oneClickV2Service';
import TradingPortalFacet from '@ui/services/contract/abis/TradingPortalFacet';
import { getContractAddress } from '@ui/services/contract/constants';
import { getTradingPortalFacet } from '@ui/services/contract/contract-instances/getTradingPortalFacet';
import type { DecreaseOrderRow } from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/types';
import type { Address } from '@ui/types/alias';
import { TxnMutateCallBackParams } from '@ui/types/tx';
import { extractErrorMessage } from '@ui/utils/extractContractError';
import { increaseGasLimit } from '@ui/utils/increaseGastLimit';
import { isNil } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useAccount, usePublicClient } from 'wagmi';

import {
  getIsDecreaseOrderUpdateParams,
  useUpdateTradeTpAndSlParams,
} from './useUpdateTradeTpAndSlParams';
import { useUpdateTradeTpAndSlToastOpts } from './useUpdateTradeTpAndSlToastOpts';

export const useUpdateTradeTpAndSl = ({
  genericTradeParams,
  activeDecreaseOrders,
  tp,
  sl,
  onSuccess,
  onError,
  onSettled,
}: {
  tp: TpSlValueState;
  sl: TpSlValueState;
  activeDecreaseOrders?: DecreaseOrderRow[];
} & TxnMutateCallBackParams &
  GenericTradeParams) => {
  const chainId = useDataChainId();

  const { onErrorToastOpts, onMutateToastOpts, onSuccessToastOpts } =
    useUpdateTradeTpAndSlToastOpts({
      genericTradeParams,
    });

  const updateTradeTpAndSlParams = useUpdateTradeTpAndSlParams({
    genericTradeParams,
    activeDecreaseOrders,
    tp,
    sl,
  });

  const invalidatePositionQuery = useInvalidatePositionQuery();

  const {
    isReady: canUseOneClickTradingV2,
    isSubmittingUpdateTpSl: isSubmittingUpdateTpSlV2,
    sendUpdateTpSl: sendUpdateTpSlV2,
  } = useOneClickTradingV2();
  const mutationToastInfo = useMutationToast();
  const [isAwaitingOneClickResult, setIsAwaitingOneClickResult] =
    useState(false);

  const { data, writeContract, isPending } = useTxnWriteContract({
    onSuccessToastOpts,
    onMutateToastOpts,
    onErrorToastOpts,
    onSuccess: () => {
      invalidatePositionQuery();

      onSuccess?.();
    },
    onError,
    onSettled,
  });

  const client = usePublicClient({
    chainId,
  });

  const { address: account } = useAccount();
  const { isSimulating, simulate } = useSimulateMutation();

  const handleOneClickUpdateTradeTpSlV2 = useCallback(async () => {
    if (!canUseOneClickTradingV2) return;
    if (isNil(updateTradeTpAndSlParams)) return;

    // Only work for legacy (non-merged) case
    if (getIsDecreaseOrderUpdateParams(updateTradeTpAndSlParams)) return;

    const [tradeHash, takeProfit, stopLoss] = updateTradeTpAndSlParams;

    setIsAwaitingOneClickResult(true);
    mutationToastInfo.onMutate({
      title: 'In Progress',
      description: '⚡️ One-click V2 trade TP/SL update in progress',
    });

    try {
      const intentHash = await sendUpdateTpSlV2(
        tradeHash,
        takeProfit,
        stopLoss,
      );

      const tradingStatus = await pollV2IntentStatus(intentHash);
      if (!tradingStatus.success) {
        throw new Error(
          tradingStatus.reason ||
            'One-click V2 trade TP/SL update execution failed',
        );
      }

      invalidatePositionQuery();

      mutationToastInfo.onSuccess({
        title: '⚡️ Completed',
        description:
          'Trade TP/SL updated successfully via one-click V2',
        txHash: tradingStatus.txnHash as Address,
      });

      onSuccess?.();

      return tradingStatus.txnHash;
    } catch (error) {
      const message =
        error instanceof Error
          ? extractErrorMessage(error.message) ?? error.message
          : undefined;

      mutationToastInfo.onError({
        errorMsg: message,
        onErrorToastOpts: {
          description:
            'One-click V2 trade TP/SL update failed, please try again',
        },
      });

      throw error;
    } finally {
      mutationToastInfo.onSettled();
      setIsAwaitingOneClickResult(false);
    }
  }, [
    canUseOneClickTradingV2,
    invalidatePositionQuery,
    mutationToastInfo,
    onSuccess,
    sendUpdateTpSlV2,
    updateTradeTpAndSlParams,
  ]);

  const write = useCallback(async () => {
    // OneClick V2 path (priority) — only for legacy (non-merged) case
    if (
      canUseOneClickTradingV2 &&
      updateTradeTpAndSlParams &&
      !getIsDecreaseOrderUpdateParams(updateTradeTpAndSlParams)
    ) {
      return handleOneClickUpdateTradeTpSlV2();
    }

    // On-chain path
    const leverUpAddress = getContractAddress('LeverUp', chainId);
    if (isNil(updateTradeTpAndSlParams) || isNil(leverUpAddress)) return;
    if (!client) {
      throw new Error('missing client');
    }
    let estimateGasResult;

    const { isSuccess } = await simulate(async () => {
      const contract = getTradingPortalFacet({
        chainId,
        publicClient: client,
      });
      if (!getIsDecreaseOrderUpdateParams(updateTradeTpAndSlParams)) {
        estimateGasResult = await contract.estimateGas.updateTradeTpAndSl(
          [...updateTradeTpAndSlParams],
          {
            account,
          },
        );
        return;
      }

      const gasResults = await Promise.all([
        ...(updateTradeTpAndSlParams.updateOrders.length > 0
          ? [
              contract.estimateGas.batchUpdateDecreaseOrders(
                [updateTradeTpAndSlParams.updateOrders],
                {
                  account,
                },
              ),
            ]
          : []),
        ...updateTradeTpAndSlParams.cancelOrderHashes.map((orderHash) =>
          contract.estimateGas.cancelDecreaseOrder([orderHash], {
            account,
          }),
        ),
        ...(updateTradeTpAndSlParams.createOrders.length > 0
          ? [
              contract.estimateGas.batchCreateDecreaseOrders(
                [genericTradeParams.id, updateTradeTpAndSlParams.createOrders],
                {
                  account,
                },
              ),
            ]
          : []),
      ]);
      estimateGasResult = gasResults.reduce((acc, value) => acc + value, 0n);
    });
    if (!isSuccess) return;

    const gasLimit = increaseGasLimit(estimateGasResult);

    if (!getIsDecreaseOrderUpdateParams(updateTradeTpAndSlParams)) {
      return writeContract({
        abi: TradingPortalFacet,
        address: leverUpAddress,
        functionName: 'updateTradeTpAndSl',
        args: [...updateTradeTpAndSlParams],
        gas: gasLimit,
      });
    }

    if (updateTradeTpAndSlParams.updateOrders.length > 0) {
      await writeContract({
        abi: TradingPortalFacet,
        address: leverUpAddress,
        functionName: 'batchUpdateDecreaseOrders',
        args: [updateTradeTpAndSlParams.updateOrders],
        gas: gasLimit,
      });
    }

    for (const orderHash of updateTradeTpAndSlParams.cancelOrderHashes) {
      await writeContract({
        abi: TradingPortalFacet,
        address: leverUpAddress,
        functionName: 'cancelDecreaseOrder',
        args: [orderHash],
        gas: gasLimit,
      });
    }

    if (updateTradeTpAndSlParams.createOrders.length > 0) {
      await writeContract({
        abi: TradingPortalFacet,
        address: leverUpAddress,
        functionName: 'batchCreateDecreaseOrders',
        args: [genericTradeParams.id, updateTradeTpAndSlParams.createOrders],
        gas: gasLimit,
      });
    }
  }, [
    account,
    canUseOneClickTradingV2,
    chainId,
    client,
    genericTradeParams.id,
    handleOneClickUpdateTradeTpSlV2,
    simulate,
    updateTradeTpAndSlParams,
    writeContract,
  ]);

  return useMemo(() => {
    return {
      data,
      writeContract: write,
      isPending:
        isSimulating ||
        isPending ||
        isSubmittingUpdateTpSlV2 ||
        isAwaitingOneClickResult,
    };
  }, [
    data,
    isPending,
    isSimulating,
    isSubmittingUpdateTpSlV2,
    isAwaitingOneClickResult,
    write,
  ]);
};


